import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@trillion/ui/components/ui/table";
import { PageHeader } from "@trillion/ui/components/admin/page-header";
import { DataTableWrapper } from "@trillion/ui/components/admin/data-table-wrapper";
import { ConfirmDialog } from "@trillion/ui/components/admin/confirm-dialog";
import { useDataTableState } from "@trillion/ui";
import { adminApi } from "@/lib/admin-api";
import { describeApiError } from "@/lib/api-error";
import { getNetworkColumns } from "@/components/networks/network-columns";
import type { ProductSource } from "@/types";

const PAGE_SIZE = 20;

export function NetworksPage() {
  const navigate = useNavigate();

  const t = useDataTableState<ProductSource>({
    fetcher: async (p) => {
      const result = await adminApi.sources.list({
        page: p.page,
        pageSize: p.pageSize,
        search: p.search || undefined,
        sortBy: p.sortBy,
        sortDesc: p.sortDesc,
      });
      return { items: result.items, totalCount: result.total_count };
    },
    onDelete: async (network) => {
      // A 409 here means brands still reference the network — surfaced verbatim via describeApiError.
      await adminApi.sources.delete(network.source_id);
      toast.success("Network deleted");
    },
    pageSize: PAGE_SIZE,
    onError: (err) =>
      toast.error("Request failed", {
        description: describeApiError(err),
      }),
  });

  const { setDeleteTarget } = t;
  const columns = useMemo(
    () =>
      getNetworkColumns({
        onEdit: (network) => navigate(`/networks/${network.source_id}`),
        onDelete: (network) => setDeleteTarget(network),
      }),
    [navigate, setDeleteTarget]
  );

  const table = useReactTable({
    data: t.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Networks"
        description="Manage affiliate networks (product sources)"
        actions={
          <Button onClick={() => navigate("/networks/new")}>
            <Plus className="mr-2 size-4" />
            New Network
          </Button>
        }
      />

      <DataTableWrapper
        title="All Networks"
        description={`${t.totalCount} network${t.totalCount !== 1 ? "s" : ""} total`}
        isLoading={t.isLoading}
        toolbar={
          <div className="flex items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search networks..."
                value={t.search}
                onChange={(e) => t.setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        }
        pagination={
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {t.page} of {t.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(Math.max(1, t.page - 1))}
                disabled={t.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => t.setPage(t.page + 1)}
                disabled={t.page >= t.totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        }
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No networks found.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DataTableWrapper>

      <ConfirmDialog
        open={t.deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) t.setDeleteTarget(null);
        }}
        title="Delete Network"
        description={`Are you sure you want to delete "${t.deleteTarget?.name}"? Brands under this network must be deleted first.`}
        confirmLabel="Delete"
        onConfirm={t.confirmDelete}
        variant="destructive"
        isLoading={t.isDeleting}
      />
    </div>
  );
}
